import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { midas } from '@resurs/midas'

const TextInput = props => {
  const [isDirty, setIsDirty] = useState(false)
  const [isActive, setIsActive] = useState(false)
  const [isValid, setIsValid] = useState(props.valid)
  const [hasFormatError, setHasFormatError] = useState(false)
  const [hasRequiredError, setHasRequiredError] = useState(false)
  const [isFirst] = useState(true)
  const [isRequired, setIsRequired] = useState(!!props.required)
  const [value, setValue] = useState(props.value)
  const [shouldAdditionalValidationError, setShouldAdditionalValidationError] =
    useState(false)

  useEffect(() => {
    setValue(props.value)
  }, [props.value, props.required])

  useEffect(() => {
    setIsRequired(props.required)
  }, [props.required])

  useEffect(() => {
    props.isValid({ key: props.id, valid: isValid })
  }, [isValid])

  const handleInvalid = e => {
    e.preventDefault()
    setIsRequired(true)
  }

  const handleChange = e => {
    setValue(e.target.value)
    props.onChange(e)
  }

  const test = (targetValue, valid = props.valid) => {
    const expr = props.format && props.format.regex ? props.format.regex : null
    let localIsValid
    let formatError = false
    let requiredError = false

    if (valid) {
      localIsValid = true
    } else if (isRequired && targetValue === '') {
      localIsValid = false
      requiredError = true
    } else if (!isRequired && targetValue === '') {
      localIsValid = true
      requiredError = false
      formatError = false
    } else if (targetValue && expr) {
      localIsValid = new RegExp(expr).test(targetValue)
      formatError = !localIsValid
    } else if (!expr) {
      localIsValid = true
    } else {
      localIsValid = false
    }

    const additionalValidationError =
      localIsValid &&
      props.additionalValidation(targetValue, localIsValid, props.id)

    localIsValid = localIsValid && !additionalValidationError

    setIsDirty(true)
    setHasFormatError(formatError)
    setHasRequiredError(requiredError)
    setShouldAdditionalValidationError(additionalValidationError)
    setIsValid(localIsValid)
    props.onValidChange(props.id, localIsValid)
  }

  useEffect(() => {
    if (value) {
      test(value)
    }
  }, [])

  const textInputProps = {
    id: props.id,
    name: props.id,
    placeholder: props.placeholder,
    readOnly: props.readonly,
    disabled: props.disabled,
    required: props.required,
    type: props.type,
    value: props.value,
    pattern:
      props.format && props.format.regex ? props.format.regex : undefined,
    autoComplete: props.disableAutocomplete ? 'off' : undefined,
    autoFocus: props.autoFocus ? 'autofocus' : undefined,
    onChange: e => {
      if (!props.disabled) {
        handleChange(e)
      }
    },
    onClick: e => {
      if (!props.disabled) {
        props.onClick(e)
      }
    },
    onKeyUp: e => {
      if (!props.skipValidation) {
        test(e.target.value)
      }
    },
    onTouchEnd: e => {
      if (!props.skipValidation) {
        setTimeout(() => test(e.target.value), 100)
      }
    },
    onFocus: () => {
      if (props.valid) {
        setIsValid(true)
        setIsDirty(true)
      }
      setIsActive(true)
    },
    onBlur: e => {
      setIsActive(false)
      if (!props.skipValidation) {
        test(e.target.value)
      }
    },
    onInvalid: e => {
      if (!props.disabled) {
        handleInvalid(e)
      }
    },
  }

  const wrapperClassNames = [
    midas.formGroup,
    isDirty && !isValid && isRequired && hasRequiredError && midas.hasWarning,
    isDirty && !isValid && !isActive && midas.hasDanger,
    isDirty && !isValid && !isFirst && isActive && midas.hasWarning,
    isDirty && isValid && value !== '' && midas.hasSuccess,
    isDirty && isValid && value !== '' && midas.hasSuccess,
    shouldAdditionalValidationError && midas.hasDanger,
  ]
    .filter(val => val)
    .join(' ')

  const textInputClassNames = [
    midas.formInput,
    props.size === 'small' && midas.inputSm,
    props.size === 'large' && midas.inputLg,
    props.size === 'xlarge' && midas.inputXl,
  ]
    .filter(val => val)
    .join(' ')

  const labelText = props.label ? (
    <label className={midas.formLabel} htmlFor={props.id}>
      {props.label} {props.required && <span>*</span>}
    </label>
  ) : null

  const hintValue = props.hint ? (
    <span className={midas.formInputHint}>{props.hint}</span>
  ) : null

  const errorValue = isDirty &&
    !isValid &&
    !isActive &&
    hasFormatError &&
    !hasRequiredError && (
      <span className={midas.formInputHint}>{props.format.error}</span>
    )

  const additionalErrorValue = shouldAdditionalValidationError && (
    <span className={midas.formInputHint}>
      {shouldAdditionalValidationError}
    </span>
  )

  const requiredValue = isDirty &&
    !isActive &&
    isRequired &&
    hasRequiredError && (
      <span className={midas.formInputHint}>{props.required}</span>
    )

  const inputGroup = (
    <div className={midas.inputGroup}>
      <input className={textInputClassNames} {...textInputProps} />
      {props.children}
    </div>
  )

  const input = <input className={textInputClassNames} {...textInputProps} />

  return (
    <div
      className={wrapperClassNames}
      data-cy={props.dataCy}
      style={{ ...props.style }}
    >
      {labelText}
      {props.children ? inputGroup : input}
      {hintValue}
      {errorValue}
      {requiredValue}
      {additionalErrorValue}
    </div>
  )
}

TextInput.propTypes = {
  children: PropTypes.node,
  disabled: PropTypes.bool,
  readonly: PropTypes.bool,
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  type: PropTypes.string,
  size: PropTypes.oneOf(['small', 'normal', 'large', 'xlarge']),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  valid: PropTypes.bool,
  required: PropTypes.string,
  format: props => {
    if (!props.required) {
      if (!props.skipValidation && !props.format) {
        return new Error(
          'Midas Error: Text Input requires a format, otherwise skipValidation must be true',
        )
      }
      return undefined
    }
    return undefined
  },
  disableAutocomplete: PropTypes.bool,
  isValid: PropTypes.func,
  hint: PropTypes.string,
  skipValidation: PropTypes.bool,
  autoFocus: PropTypes.bool,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  onValidChange: PropTypes.func,
  additionalValidation: PropTypes.func,
  dataCy: PropTypes.string,
}
TextInput.defaultProps = {
  children: null,
  disabled: false,
  readonly: false,
  placeholder: null,
  value: '',
  type: 'text',
  size: 'normal',
  valid: false,
  required: null,
  format: null,
  skipValidation: false,
  label: null,
  hint: null,
  disableAutocomplete: false,
  autoFocus: false,
  dataCy: null,
  isValid: () => {},
  onChange: () => {},
  onClick: () => {},
  onValidChange: () => {},
  additionalValidation: () => {},
}

export default TextInput
